import { useEffect, useId, useState, type FormEvent } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router';
import {
  CONTACT_FORM_FIELDS,
  type ContactFormField,
  type ContactValues,
  type SyncOp,
} from '@wr/shared';
import { ApiError, errorMessage, get } from '../../api.js';
import {
  ContactForm,
  EMPTY_CONTACT,
  toFieldErrors,
  type ContactDraft,
  type FieldErrors,
} from '../../components/ContactForm.js';
import { CardThumb, WithCard } from '../capture/CardImage.js';
import { Icon } from '../../components/Icon.js';
import { DeleteDialog } from '../../components/DeleteDialog.js';
import { useToast } from '../../components/Toast.js';
import { Dialog, Field, Notice, formatDateTime } from '../../components/ui.js';
import {
  LISTS,
  contactPushBlockers,
  marketLabel,
  optionLabel,
  validateContact,
} from '../../lists.js';
import { useOffline } from '../../offline/context.js';
import { newId } from '../../offline/ids.js';
import type { LocalRecord } from '../../offline/store.js';
import { useSignedIn } from '../../session.js';
import { HubSpotTasks } from '../leads/LeadSheet.js';
import {
  avatarColor,
  fullName,
  initialsOf,
  loadContact,
  type ContactSheetData,
  type SheetLead,
} from './data.js';

type Mode = 'view' | 'edit' | 'verify';

const FIELD_LABELS: Record<ContactFormField, string> = {
  first_name: 'First name',
  last_name: 'Last name',
  email: 'Email',
  company: 'Company',
  job_title: 'Job title',
  mobile: 'Mobile',
  office_phone: 'Office phone',
  linkedin_url: 'LinkedIn',
  country: 'Country',
  applications: 'Applications',
  interest: 'Interest',
  role: 'Role',
  pain: 'Pain',
  wants_content: 'Brochure and video',
  note: 'Note',
};

const str = (v: unknown) => (typeof v === 'string' ? v : '');

/** The contact as the form edits it. */
function draftOf(c: LocalRecord): ContactDraft {
  return {
    ...EMPTY_CONTACT,
    first_name: str(c.first_name),
    last_name: str(c.last_name),
    email: str(c.email),
    company: str(c.company),
    job_title: str(c.job_title),
    mobile: str(c.mobile),
    office_phone: str(c.office_phone),
    linkedin_url: str(c.linkedin_url),
    country: str(c.country),
    applications: Array.isArray(c.applications) ? (c.applications as string[]) : [],
    interest: str(c.interest),
    role: str(c.role),
    pain: str(c.pain),
    wants_content: c.wants_content === true,
    note: str(c.note),
  };
}

/** A value as stored: null for an empty field, [] and false for empty lists and boxes. */
function normalized(field: string, value: unknown): unknown {
  if (field === 'wants_content') return value === true;
  if (field === 'applications') return Array.isArray(value) ? value : [];
  return value === undefined || value === null || value === '' ? null : value;
}

/** Only what changed is sent; an emptied field is sent as null. */
function changes(before: ContactDraft, after: ContactValues): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const field of CONTACT_FORM_FIELDS) {
    const was = normalized(field, before[field as keyof ContactDraft]);
    const now = normalized(field, (after as unknown as Record<string, unknown>)[field]);
    if (JSON.stringify(was) !== JSON.stringify(now)) out[field] = now;
  }
  return out;
}

/**
 * The contact sheet (brief section 6.6), a window over People as in
 * docs/prototype.html: the HubSpot state, the header with Call, Email and
 * LinkedIn, the Contact block (every field, "Not given" when empty), the
 * Leads block, the scanned card, the exit criteria. Edit opens the same
 * form, prefilled; Verify contact shows the fields the scan was not sure
 * of, against the card. Every change is saved on the device first.
 */
export function ContactSheet({ mode }: { mode: Mode }) {
  const { id = '' } = useParams();
  return <Sheet key={id} id={id} mode={mode} />;
}

function Sheet({ id, mode }: { id: string; mode: Mode }) {
  const { user, can, refresh } = useSignedIn();
  const { store, save, counts } = useOffline();
  const toast = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const [params] = useSearchParams();
  // Opened from a lead sheet or To do: closing goes back there.
  const from = (location.state as { from?: string } | null)?.from;
  const [data, setData] = useState<ContactSheetData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const back = (path = '') =>
    navigate(
      { pathname: `/people/contacts/${id}${path}`, search: params.toString() },
      { replace: true, state: { from } },
    );
  const close = () => navigate(from ?? { pathname: '/people', search: params.toString() });

  useEffect(() => {
    let current = true;
    loadContact(id, store, { id: user.id, name: `${user.firstName} ${user.lastName}` })
      .then((loaded) => {
        if (!current) return;
        if (loaded) setData(loaded);
        else setError('This contact does not exist, or it was deleted.');
      })
      .catch((err: unknown) => {
        if (err instanceof ApiError && err.status === 401) void refresh();
        else if (current) {
          setError(
            err instanceof ApiError && err.status === 404
              ? 'This contact does not exist, or it was deleted.'
              : errorMessage(err),
          );
        }
      });
    return () => {
      current = false;
    };
    // counts: a change saved or sent (a lead created, a link) reloads the sheet.
  }, [id, store, user, refresh, counts]);

  if (error || !data) {
    return (
      <Dialog title="Contact" onClose={close}>
        {error ? (
          <Notice kind="error">{error}</Notice>
        ) : (
          <div className="spinner" aria-label="Loading" />
        )}
      </Dialog>
    );
  }

  const c = data.contact;
  const own = !c.created_by || c.created_by === user.id;
  const canChange = can('contacts.update.all') || (own && can('contacts.update.own'));
  const canDelete = can('contacts.delete.all') || (own && can('contacts.delete.own'));
  const flagged = (
    Array.isArray(c.needs_verification) ? c.needs_verification : []
  ) as ContactFormField[];

  async function apply(fields: Record<string, unknown>, message: string) {
    const op: SyncOp = {
      opId: newId(),
      entity: 'contact',
      id,
      action: 'upsert',
      changedAt: new Date().toISOString(),
      fields,
    };
    await save(op);
    setData((d) => (d ? { ...d, contact: { ...d.contact, ...fields } } : d));
    toast(message);
  }

  async function remove(alsoHubSpot: boolean) {
    await save({
      opId: newId(),
      entity: 'contact',
      id,
      action: 'delete',
      changedAt: new Date().toISOString(),
      ...(alsoHubSpot ? { alsoHubSpot: true } : {}),
    });
    toast(`Contact deleted: ${fullName(c) || str(c.email) || 'contact'}`);
    close();
  }

  if (mode === 'edit' && canChange) {
    return (
      <EditContact
        contact={c}
        onCancel={() => back()}
        onSave={async (fields) => {
          await apply(fields, `Contact saved: ${fullName({ ...c, ...fields })}`);
          back();
        }}
      />
    );
  }

  if (mode === 'verify' && flagged.length > 0 && canChange) {
    return (
      <VerifyContact
        data={data}
        flagged={flagged}
        onLater={() => back()}
        onConfirm={async (fields) => {
          await apply({ ...fields, needs_verification: [] }, 'Contact checked.');
          back();
        }}
      />
    );
  }

  const name = fullName(c);
  const reading = c.reading_status === 'waiting' || c.reading_status === 'reading';
  const phone = str(c.mobile) || str(c.office_phone);
  const enriched = Array.isArray(c.ai_enriched_fields) && c.ai_enriched_fields.length > 0;

  return (
    <Dialog
      title="Contact"
      onClose={close}
      footer={
        <>
          {canDelete && (
            <button
              type="button"
              className="btn danger left"
              onClick={() => setConfirmDelete(true)}
            >
              Delete
            </button>
          )}
          {canChange && (
            <button type="button" className="btn" onClick={() => back('/edit')}>
              Edit
            </button>
          )}
          {data.hubspotUrl && (
            <a className="btn" href={data.hubspotUrl} target="_blank" rel="noopener noreferrer">
              Open in HubSpot
            </a>
          )}
          <button type="button" className="btn" onClick={close}>
            Done
          </button>
        </>
      }
    >
      <div id="ct-sheet" style={{ display: 'contents' }}>
        {data.offline && (
          <Notice kind="info">No connection: this is the copy kept on this device.</Notice>
        )}
        <HubSpotState contact={c} />
        {c.hubspot_status !== 'synced' && <AlreadyInHubSpot id={id} />}
        <div className="ct-head">
          <span
            className="av lg"
            style={{ background: avatarColor(name || str(c.company)) }}
            aria-hidden="true"
          >
            {initialsOf(c)}
          </span>
          <div>
            <b>{name || (reading ? 'Reading the card…' : str(c.email) || 'No name yet')}</b>
            <span>{[str(c.job_title), str(c.company)].filter(Boolean).join(', ')}</span>
            <span className="actions" style={{ gap: 6, marginTop: 4 }}>
              <span className="fb f-contact">CONTACT</span>
              {reading && <span className="badge b-warm">Reading</span>}
              {c.reading_status === 'failed' && (
                <span className="badge b-warm">Could not read</span>
              )}
              {flagged.length > 0 && (
                <span className="badge b-warm">{flagged.length} to verify</span>
              )}
              {enriched && <span className="badge b-ok">✨ AI enriched</span>}
            </span>
          </div>
        </div>
        {(c.source === 'scan' || str(c.card_image_key) || Boolean(c.card_removed_at)) && (
          <CardThumb contact={c} />
        )}
        {reading && (
          <p className="note" style={{ margin: 0 }}>
            The card is being read: its fields fill in by themselves in a moment.
          </p>
        )}
        {c.reading_status === 'failed' && (
          <Notice kind="warn">
            The card could not be read. Type the contact from the card with Edit.
          </Notice>
        )}
        {flagged.length > 0 && canChange && (
          <Notice kind="warn">
            The scan was not sure about {flagged.map((f) => FIELD_LABELS[f] ?? f).join(', ')}.{' '}
            <button type="button" className="linkbtn" onClick={() => back('/verify')}>
              Check against the card
            </button>
          </Notice>
        )}
        {(phone || str(c.email) || str(c.linkedin_url)) && (
          <div className="quick-contact">
            {phone && (
              <a className="btn" href={`tel:${phone.replace(/\s+/g, '')}`}>
                <Icon name="phone" />
                Call
              </a>
            )}
            {str(c.email) && (
              <a className="btn" href={`mailto:${str(c.email)}`}>
                <Icon name="mail" />
                Email
              </a>
            )}
            {str(c.linkedin_url) && (
              <a
                className="btn"
                href={str(c.linkedin_url)}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Icon name="link" />
                LinkedIn
              </a>
            )}
          </div>
        )}
        <ContactBlock data={data} meId={user.id} />
        <LeadsBlock
          leads={data.leads}
          onOpen={(lead) =>
            navigate(`/people/leads/${lead}`, { state: { from: `/people/contacts/${id}` } })
          }
          onCreate={can('leads.create') ? () => back('/lead') : undefined}
          onLink={
            can('leads.update.own') || can('leads.update.all')
              ? () => back('/link-lead')
              : undefined
          }
          onVoice={
            can('voice.qualify') && data.leads.length === 0 ? () => back('/voice') : undefined
          }
        />
        <ExitCriteria contact={c} leads={data.leads} />
      </div>
      {confirmDelete && (
        <DeleteDialog
          title="Delete this contact?"
          inHubSpot={Boolean(c.hubspot_id)}
          hubspotLabel="Also delete it in HubSpot, and its company if the portal created it and nothing else is on it (they can be restored there for 90 days)."
          onCancel={() => setConfirmDelete(false)}
          onDelete={(alsoHubSpot) => void remove(alsoHubSpot)}
        >
          It will be removed from the portal. Its leads keep their history.
        </DeleteDialog>
      )}
    </Dialog>
  );
}

function HubSpotState({ contact: c }: { contact: LocalRecord }) {
  if (c.hubspot_status === 'synced') {
    return (
      <div className="hs-state ok">
        <b>In HubSpot since {formatDateTime(str(c.synced_at)).split(',')[0]}</b>
        <HubSpotTasks record={c} />
        <span>Changes made here update HubSpot, and changes made in HubSpot come back here.</span>
        {Boolean(c.hubspot_pulled_at) && (
          <span>Updated from HubSpot {formatDateTime(str(c.hubspot_pulled_at))}</span>
        )}
      </div>
    );
  }
  if (c.reading_status === 'waiting' || c.reading_status === 'reading') {
    return (
      <div className="hs-state wait">
        <b>Not in HubSpot yet</b>
        <span>The card is being read.</span>
      </div>
    );
  }
  const blockers = contactPushBlockers(c);
  if (blockers.length > 0) {
    return (
      <div className="hs-state wait">
        <b>Not in HubSpot yet: to complete first</b>
        <span>{blockers.map((b) => b.message).join(' ')}</span>
      </div>
    );
  }
  if (c.hubspot_status === 'error') {
    return (
      <div className="hs-state wait">
        <b>HubSpot did not take it yet</b>
        <span>{str(c.hubspot_error) || 'The portal tries again by itself.'}</span>
      </div>
    );
  }
  return (
    <div className="hs-state wait">
      <b>Sending to HubSpot…</b>
    </div>
  );
}

/**
 * "Already in HubSpot" (brief section 6.6): HubSpot knows this email. Asked
 * once the sheet is open; HubSpot not answering only leaves it out.
 */
function AlreadyInHubSpot({ id }: { id: string }) {
  const [found, setFound] = useState<{ name: string; url: string | null } | null>(null);
  useEffect(() => {
    let current = true;
    get<{ found: { name: string; url: string | null } | null }>(
      `/people/contacts/${encodeURIComponent(id)}/hubspot`,
    )
      .then((r) => current && setFound(r.found))
      .catch(() => undefined);
    return () => {
      current = false;
    };
  }, [id]);
  if (!found) return null;
  return (
    <div className="dup">
      <b>Already in HubSpot</b>
      <span>
        {found.name || 'This person'} is already a contact in HubSpot. Sending this one updates it:
        no duplicate is created.
      </span>
      {found.url && (
        <span className="actions">
          <a className="btn sm" href={found.url} target="_blank" rel="noopener noreferrer">
            Open in HubSpot
          </a>
        </span>
      )}
    </div>
  );
}

function Value({ value }: { value: string }) {
  return value ? <>{value}</> : <span className="note">Not given</span>;
}

function ContactBlock({ data, meId }: { data: ContactSheetData; meId: string }) {
  const c = data.contact;
  const apps = Array.isArray(c.applications) ? (c.applications as string[]) : [];
  const captured = [
    c.created_by === meId || !c.created_by
      ? `${data.createdByName ?? 'You'} (you)`
      : (data.createdByName ?? 'A former user'),
    str(c.created_at)
      ? new Date(str(c.created_at)).toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
        })
      : '',
  ]
    .filter(Boolean)
    .join(', ');
  const copiedTo = data.leads.find((l) => l.role && c.role);
  const how: Record<string, string> = {
    scan: 'Scanned',
    manual: 'Typed by the seller',
    kiosk_form: 'Typed by the visitor (Hand the tablet)',
    kiosk_visitor: 'Booth kiosk',
    hubspot: 'From HubSpot',
  };
  return (
    <div className="formsec ct-block">
      <h3>Contact</h3>
      <dl className="kv">
        <dt>Company</dt>
        <dd>
          <Value value={str(c.company)} />
        </dd>
        <dt>Job title</dt>
        <dd>
          <Value value={str(c.job_title)} />
        </dd>
        <dt>Email</dt>
        <dd>
          {str(c.email) ? (
            <a href={`mailto:${str(c.email)}`}>{str(c.email)}</a>
          ) : (
            <Value value="" />
          )}
        </dd>
        <dt>Mobile</dt>
        <dd>
          {str(c.mobile) ? (
            <a href={`tel:${str(c.mobile).replace(/\s+/g, '')}`}>{str(c.mobile)}</a>
          ) : (
            <Value value="" />
          )}
        </dd>
        <dt>Office phone</dt>
        <dd>
          {str(c.office_phone) ? (
            <a href={`tel:${str(c.office_phone).replace(/\s+/g, '')}`}>{str(c.office_phone)}</a>
          ) : (
            <Value value="" />
          )}
        </dd>
        <dt>LinkedIn</dt>
        <dd>
          {str(c.linkedin_url) ? (
            <a href={str(c.linkedin_url)} target="_blank" rel="noopener noreferrer">
              {str(c.linkedin_url).replace(/^https?:\/\/(www\.)?/, '')}
            </a>
          ) : (
            <Value value="" />
          )}
        </dd>
        <dt>Country</dt>
        <dd>
          <Value value={str(c.country)} />
        </dd>
        <dt>Applications</dt>
        <dd>
          <Value value={apps.map(marketLabel).join(', ')} />
        </dd>
        <dt>Interest</dt>
        <dd>
          <Value value={optionLabel(LISTS.interestOptions, str(c.interest))} />
        </dd>
        <dt>Role</dt>
        <dd>
          <Value value={optionLabel(LISTS.roleOptions, str(c.role))} />
        </dd>
        <dt>Pain</dt>
        <dd>
          <Value value={str(c.pain)} />
        </dd>
        <dt>Brochure and video</dt>
        <dd>{c.wants_content === true ? 'Yes, a HubSpot task is created' : 'Not requested'}</dd>
        <dt>Note</dt>
        <dd style={{ whiteSpace: 'pre-wrap' }}>
          <Value value={str(c.note)} />
        </dd>
        <dt>Event</dt>
        <dd>
          <Value value={data.eventCode ?? ''} />
        </dd>
        <dt>Captured by</dt>
        <dd>{captured}</dd>
        <dt>How</dt>
        <dd>{how[str(c.source)] ?? str(c.source)}</dd>
        {str(c.consent_at) !== '' && (
          <>
            <dt>Consent</dt>
            <dd>
              Given {formatDateTime(str(c.consent_at))} (sentence {str(c.consent_text_version)})
            </dd>
          </>
        )}
      </dl>
      {copiedTo && (
        <p className="note" style={{ margin: 0 }}>
          Copied to <b>{copiedTo.deal_name || copiedTo.company_name || 'the lead'}</b> when the lead
          was created. The lead keeps its own copy.
        </p>
      )}
    </div>
  );
}

const QUALIFICATION: Record<string, [string, string]> = {
  qualified: ['Looks qualified', 'b-ok'],
  disqualified: ['Not qualified', 'b-warm'],
};

function LeadsBlock({
  leads,
  onOpen,
  onCreate,
  onLink,
  onVoice,
}: {
  leads: SheetLead[];
  onOpen(id: string): void;
  onCreate?: () => void;
  onLink?: () => void;
  /** Qualify by voice (step 5), while the contact has no lead. */
  onVoice?: () => void;
}) {
  return (
    <div className="formsec lk-sec">
      <h3>Leads</h3>
      {leads.length === 0 && (
        <p className="note" style={{ margin: 0 }}>
          No lead yet. A contact stays a marketing contact until a project is documented.
        </p>
      )}
      {leads.map((l) => {
        const [label, badge] = (l.qualification && QUALIFICATION[l.qualification]) || [
          'To document',
          'b-warm',
        ];
        return (
          <button key={l.id} type="button" className="lk-card" onClick={() => onOpen(l.id)}>
            <span className="lk-top">
              <b>{l.deal_name || l.company_name || 'Lead'}</b>
              <span className={`badge ${badge}`}>{label}</span>
            </span>
            <span className="note">
              {[optionLabel(LISTS.roleOptions, l.role ?? ''), l.pain ?? '']
                .filter(Boolean)
                .join(', ')}
            </span>
            <span className="lk-meta">
              <span>
                {l.hubspot_status === 'synced'
                  ? 'In HubSpot'
                  : l.qualification
                    ? 'Ready'
                    : 'To complete'}
              </span>
              <span>{l.is_primary ? 'Primary contact' : 'Linked contact'}</span>
            </span>
          </button>
        );
      })}
      {(onCreate || onLink || onVoice) && (
        <div className="actions">
          {onCreate && (
            <button type="button" className="btn sm primary" onClick={onCreate}>
              + Lead
            </button>
          )}
          {onLink && (
            <button type="button" className="btn sm" onClick={onLink}>
              Link to an existing lead
            </button>
          )}
          {onVoice && (
            <button type="button" className="btn sm" onClick={onVoice}>
              <Icon name="mic" />
              Qualify by voice
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function ExitCriteria({ contact: c, leads }: { contact: LocalRecord; leads: SheetLead[] }) {
  const item = (ok: boolean, text: string, small?: string) => (
    <li className={ok ? 'ok' : ''}>
      <span className="xb">{ok ? '✓' : ''}</span>
      <span>
        {text}
        {small && <small>{small}</small>}
      </span>
      <span className="xa">auto</span>
    </li>
  );
  return (
    <>
      <div className="formsec exitbox">
        <h3>Tradeshow: exit criteria</h3>
        <ul className="exit">
          {item(
            Boolean(c.first_name && c.last_name && c.email),
            'Contact information captured (name, email)',
          )}
          {item(true, 'Contact added in the scanner')}
          {item(
            c.hubspot_status === 'synced',
            'Added in HubSpot as a marketing contact, with the company',
            'At the push or at the wrap-up',
          )}
        </ul>
      </div>
      {leads.length > 0 && (
        <div className="formsec exitbox">
          <h3>Lead</h3>
          <ul className="exit">
            {item(true, 'Lead created')}
            {item(
              leads.some((l) => l.qualification),
              'Discovery Capture complete',
            )}
          </ul>
        </div>
      )}
    </>
  );
}

/** Edit contact: the same form as Add contact, prefilled, More info open. */
function EditContact({
  contact,
  onCancel,
  onSave,
}: {
  contact: LocalRecord;
  onCancel(): void;
  onSave(fields: Record<string, unknown>): Promise<void>;
}) {
  const before = draftOf(contact);
  const [draft, setDraft] = useState<ContactDraft>(before);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [moreOpen, setMoreOpen] = useState(true);
  const [saving, setSaving] = useState(false);
  const [failed, setFailed] = useState<string | null>(null);
  const formId = useId();

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (saving) return;
    const result = validateContact(draft);
    if (!result.ok) {
      setErrors(toFieldErrors(result.errors));
      return;
    }
    const fields = changes(before, result.value);
    if (Object.keys(fields).length === 0) return onCancel();
    // A field the seller retyped is checked: it leaves the list to verify.
    const flagged = Array.isArray(contact.needs_verification)
      ? (contact.needs_verification as string[])
      : [];
    const still = flagged.filter((f) => !(f in fields));
    if (still.length !== flagged.length) fields.needs_verification = still;
    setSaving(true);
    try {
      await onSave(fields);
    } catch {
      setFailed('This device could not save the change. Nothing was lost: try again.');
      setSaving(false);
    }
  }

  return (
    <Dialog
      title="Edit contact"
      className={contact.source === 'scan' ? 'wide' : undefined}
      onClose={onCancel}
      footer={
        <>
          <button type="button" className="btn" onClick={onCancel}>
            Cancel
          </button>
          <button type="submit" form={formId} className="btn primary" disabled={saving}>
            Save
          </button>
        </>
      }
    >
      <WithCard contact={contact}>
        <form id={formId} className="dlg-form" noValidate onSubmit={(e) => void submit(e)}>
          <ContactForm
            value={draft}
            onChange={(v) => {
              setDraft(v);
              if (Object.keys(errors).length > 0) setErrors({});
            }}
            errors={errors}
            moreOpen={moreOpen}
            onMoreToggle={setMoreOpen}
          />
          {failed && <Notice kind="error">{failed}</Notice>}
        </form>
      </WithCard>
    </Dialog>
  );
}

/** Verify contact: the fields the scan was not sure of, next to the card. */
function VerifyContact({
  data,
  flagged,
  onLater,
  onConfirm,
}: {
  data: ContactSheetData;
  flagged: ContactFormField[];
  onLater(): void;
  onConfirm(fields: Record<string, unknown>): Promise<void>;
}) {
  const c = data.contact;
  const [values, setValues] = useState<Record<string, string>>(
    Object.fromEntries(flagged.map((f) => [f, str(c[f])])),
  );
  const [errors, setErrors] = useState<FieldErrors>({});
  const [saving, setSaving] = useState(false);
  const name = fullName(c);
  const reasoning = str((c.scan_meta as { email_reasoning?: string } | null)?.email_reasoning);

  async function confirm() {
    // The same rules as the form: the whole contact with the checked values.
    const merged = { ...draftOf(c), ...values };
    const result = validateContact(merged);
    const own = result.ok ? [] : result.errors.filter((e) => flagged.includes(e.field));
    if (own.length > 0) {
      setErrors(toFieldErrors(own));
      return;
    }
    const fields: Record<string, unknown> = {};
    for (const f of flagged) {
      const v = values[f]!.trim();
      if (v !== str(c[f])) fields[f] = v || null;
    }
    setSaving(true);
    await onConfirm(fields).catch(() => setSaving(false));
  }

  return (
    <Dialog
      title="Verify contact"
      className="wide"
      onClose={onLater}
      footer={
        <>
          <button type="button" className="btn left" onClick={onLater}>
            Later
          </button>
          <button
            type="button"
            className="btn primary"
            disabled={saving}
            onClick={() => void confirm()}
          >
            Confirm
          </button>
        </>
      }
    >
      <div className="ct-head">
        <span
          className="av lg"
          style={{ background: avatarColor(name || str(c.company)) }}
          aria-hidden="true"
        >
          {initialsOf(c)}
        </span>
        <div>
          <b>{name || str(c.email) || 'No name yet'}</b>
          <span>{[str(c.job_title), str(c.company)].filter(Boolean).join(', ')}</span>
          <span className="actions" style={{ gap: 6, marginTop: 4 }}>
            <span className="fb f-contact">CONTACT</span>
            <span className="badge b-warm">{flagged.length} to verify</span>
          </span>
        </div>
      </div>
      <p className="note" style={{ margin: 0 }}>
        The scan was not sure about these fields. Check them against the card, then confirm.
      </p>
      <WithCard contact={c}>
        {flagged.map((f) => (
          <div key={f} className="vflag">
            <Field
              label={FIELD_LABELS[f] ?? f}
              value={values[f] ?? ''}
              placeholder="Not read, type it in"
              error={errors[f]}
              onChange={(e) => {
                setValues((v) => ({ ...v, [f]: e.target.value }));
                setErrors({});
              }}
            />
            {f === 'email' && reasoning && (
              <p className="note" style={{ margin: '6px 0 0' }}>
                Reading of the email: {reasoning}
              </p>
            )}
          </div>
        ))}
      </WithCard>
    </Dialog>
  );
}
